import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useExtractServerError } from 'hooks';

import DirectClaimsUi from './DirectClaims.ui';
import useDirectClaimsQuery from './useDirectClaimsQuery';
import { fromIsoDate, toIsoDate } from './format';
import { DirectClaimsParams } from './DirectClaims.types';

const DEFAULT_RANGE_DAYS = 7;

function defaultStartDate() {
  const date = new Date();
  date.setDate(date.getDate() - DEFAULT_RANGE_DAYS);
  return toIsoDate(date);
}

export interface DirectClaimsFilter extends DirectClaimsParams {
  status: string;
}

export default function DirectClaims() {
  //* Hooks Init
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * The URL is what GlobeMed is asked for, so a shared link opens the same
   * report. With nothing in it the page shows the last week of claims in every
   * category rather than an empty screen.
   */
  const params: DirectClaimsParams = useMemo(
    () => ({
      startDate: searchParams.get('startDate') || defaultStartDate(),
      endDate: searchParams.get('endDate') || toIsoDate(new Date()),
      category: searchParams.get('category') || 'ALL',
      referenceNumber: searchParams.get('referenceNumber') || '',
      referenceType: searchParams.get('referenceType') || '',
      middleName: searchParams.get('middleName') || '',
    }),
    [searchParams]
  );

  //* Local State
  const [filter, setFilter] = useState<DirectClaimsFilter>({
    ...params,
    status: 'All',
  });
  const [startDate, setStartDate] = useState<Date | null>(
    fromIsoDate(params.startDate)
  );
  const [endDate, setEndDate] = useState<Date | null>(
    fromIsoDate(params.endDate)
  );
  const [page, setPage] = useState({ first: 0, rows: 50 });

  const claimsQuery = useDirectClaimsQuery(params);
  const { extractErrorMessage } = useExtractServerError();

  const allClaims = useMemo(
    () => claimsQuery.data?.data.data ?? [],
    [claimsQuery.data]
  );

  /*
   * GlobeMed has no status filter and returns the whole period in one reply,
   * so status narrowing and paging happen here, over what came back.
   */
  const claims = useMemo(
    () =>
      filter.status === 'All'
        ? allClaims
        : allClaims.filter((claim) => claim.status === filter.status),
    [allClaims, filter.status]
  );

  const statusOptions = useMemo(() => {
    const statuses = Array.from(
      new Set(allClaims.map((claim) => claim.status).filter(Boolean))
    ) as string[];

    return [
      { label: 'All statuses', value: 'All' },
      ...statuses.sort().map((status) => ({ label: status, value: status })),
    ];
  }, [allClaims]);

  //* Handlers
  function search(next: DirectClaimsFilter) {
    setPage((current) => ({ ...current, first: 0 }));
    setSearchParams({
      startDate: next.startDate,
      endDate: next.endDate,
      category: next.category,
      referenceNumber: next.referenceNumber.trim(),
      referenceType: next.referenceType.trim(),
      middleName: next.middleName.trim(),
    });
  }

  function handleSearchChange(name: string, value: string) {
    setFilter((current) => ({ ...current, [name]: value }));
  }

  function handleSubmitSearch() {
    search(filter);
  }

  function handleStartDateChange(event: any) {
    const start: Date | null = event.value ?? null;
    // The start date is required, so a cleared field keeps the last search.
    if (!start) return;

    setStartDate(start);
    const next = { ...filter, startDate: toIsoDate(start) };
    setFilter(next);
    search(next);
  }

  function handleEndDateChange(event: any) {
    const end: Date | null = event.value ?? null;

    setEndDate(end);
    // No end date means up to today, which the service fills in.
    const next = { ...filter, endDate: end ? toIsoDate(end) : '' };
    setFilter(next);
    search(next);
  }

  function handleCategoryChange(value: string) {
    const next = { ...filter, category: value, status: 'All' };
    setFilter(next);
    search(next);
  }

  function handleStatusChange(value: string) {
    setFilter((current) => ({ ...current, status: value }));
    setPage((current) => ({ ...current, first: 0 }));
  }

  function handlePageChange(event: { first: number; rows: number }) {
    setPage({ first: event.first, rows: event.rows });
  }

  return (
    <DirectClaimsUi
      claims={claims}
      totalRecords={claims.length}
      loading={claimsQuery.isFetching}
      errorMessage={
        claimsQuery.isError ? extractErrorMessage(claimsQuery.error) : ''
      }
      filter={filter}
      startDate={startDate}
      endDate={endDate}
      page={page}
      statusOptions={statusOptions}
      handlers={{
        handleSearchChange,
        handleSubmitSearch,
        handleStartDateChange,
        handleEndDateChange,
        handleCategoryChange,
        handleStatusChange,
        handlePageChange,
      }}
    />
  );
}
